import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { csrClient } from '../apollo/api/avantArteClient';
import { storefrontClient } from '../apollo/shopify/storefrontClient';
import {
  PRODUCT_TAG_NO_COA,
  PRODUCT_VARIANT_TITLE_PUBLIC,
} from '../constants/product';
import { AvantArteArtistsSearchGateway } from '../gateways/AvantArteArtistsSearchGateway';
import { ShopifyProductsGateway } from '../gateways/ShopifyProductsGateway';
import {
  GeneratedGetProductsQuery,
  GeneratedProductReleaseType,
} from '../types/generated';
import type {
  ProductStoryblok,
  ProductV2Storyblok,
  RelatedArtistStoryblok,
} from '../types/generated-storyblok';
import type { ShopifyProductPurchaseDetails } from '../types/Purchasing';
import {
  resolveShopifyAdminProductId,
  shopifyAdminIdToGraphqlId,
} from '../utils/shopifyIds';
import { shopifyToStoryblokDate } from '../utils/product';

export type ProductSourceStoryblok = ProductStoryblok | ProductV2Storyblok;
export type ProductSourceBackend =
  GeneratedGetProductsQuery['getProducts'][number];

export type ProductSourcePageData = {
  description?: unknown;
  recommendations?: MultiCurrencyProduct[];
  worksCount?: number;
  taxText?: unknown;
  pricesAndAvailability?: PricesAndAvailability;
};

export type ProductSources = {
  shopifyId: string;
  storyblokProduct?: ProductSourceStoryblok | null;
  shopifyPurchaseDetails?: ShopifyProductPurchaseDetails | null;
  shopifyProduct?: Product;
  backendProduct?: ProductSourceBackend | null;
  pageData?: ProductSourcePageData | null;
};

export type ProductContextEntry = ProductSources & {
  productType: ProductType | null;
};

type RefreshShopifyProductOptions = {
  releaseName?: string | null;
  variantIdentifier?: string;
};

type ProductContextType = {
  productsByShopifyId: Record<string, ProductContextEntry>;
  getProduct: (
    shopifyId: string | null | undefined,
  ) => ProductContextEntry | null;
  setProducts: (products: ProductSources[]) => void;
  setProduct: (product: ProductSources) => void;
  refreshShopifyProduct: (
    shopifyId: string,
    options?: RefreshShopifyProductOptions,
  ) => Promise<ShopifyProductPurchaseDetails | null>;
  refreshShopifyProducts: (
    shopifyIds: string[],
    options?: RefreshShopifyProductOptions,
  ) => Promise<void>;
};

const shopifyProductsGateway = new ShopifyProductsGateway(storefrontClient);
const EMPTY_PRODUCT_SOURCES: ProductSources[] = [];

const ProductContext = createContext<ProductContextType>({
  productsByShopifyId: {},
  getProduct: () => null,
  setProducts: () => {},
  setProduct: () => {},
  refreshShopifyProduct: () => Promise.resolve(null),
  refreshShopifyProducts: () => Promise.resolve(),
});

const RELEASE_TYPE_MAP = {
  [GeneratedProductReleaseType.Unspecified]: '',
  [GeneratedProductReleaseType.Public]: 'public-release',
  [GeneratedProductReleaseType.Draw]: 'draw-release',
  [GeneratedProductReleaseType.Private]: 'private-release',
  [GeneratedProductReleaseType.Timed]: 'timed-release',
  [GeneratedProductReleaseType.Auction]: 'auction',
  [GeneratedProductReleaseType.BlockchainAuction]: 'blockchain_auction',
  [GeneratedProductReleaseType.NftGiveaway]: 'nft_giveaway',
  [GeneratedProductReleaseType.NftRankedAuction]: 'nft_ranked_auction',
};

export const productShopifyIdKey = (
  shopifyId: string | null | undefined,
): string | null => resolveShopifyAdminProductId(shopifyId);

export const getShopifyIdFromStoryblokProduct = (
  product: ProductSourceStoryblok | null | undefined,
): string | null => {
  if (!product) return null;
  if (product.component === 'product_v2') return product.shopifyId;

  return product.id?.[0]?.id ?? null;
};

type StoryblokVariantWithShopifyId = Record<string, unknown> & {
  content: Record<string, unknown> & {
    shopifyId: string;
  };
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isStoryblokVariantWithShopifyId = (
  value: unknown,
): value is StoryblokVariantWithShopifyId => {
  if (!isRecord(value) || !isRecord(value.content)) return false;

  return typeof value.content.shopifyId === 'string';
};

type ArtistInternalIdsBySlug = Map<string, string | null>;

const relatedArtistSlug = (
  relatedArtist: RelatedArtistStoryblok,
): string | null => {
  const { artist } = relatedArtist;
  if (!artist || typeof artist === 'string') return null;

  return artist.slug ?? null;
};

const collectArtistSlugs = (products: ProductSources[]): string[] => {
  const slugs = new Set<string>();

  products.forEach(({ storyblokProduct }) => {
    const artists =
      storyblokProduct?.component === 'product_v2'
        ? storyblokProduct.artists
        : storyblokProduct?.productArtist;

    artists?.forEach((artist) => {
      const slug = relatedArtistSlug(artist);
      if (slug) slugs.add(slug);
    });
  });

  return [...slugs];
};

const withInternalArtistIds = (
  artists: RelatedArtistStoryblok[],
  artistInternalIdsBySlug: ArtistInternalIdsBySlug,
): RelatedArtistStoryblok[] =>
  artists.map((relatedArtist) => {
    const slug = relatedArtistSlug(relatedArtist);

    if (
      !slug ||
      !relatedArtist.artist ||
      typeof relatedArtist.artist === 'string'
    ) {
      return relatedArtist;
    }

    return {
      ...relatedArtist,
      artist: {
        ...relatedArtist.artist,
        internalId: artistInternalIdsBySlug.get(slug) ?? null,
      },
    } as RelatedArtistStoryblok;
  });

const toProductType = (
  {
    backendProduct,
    pageData,
    shopifyPurchaseDetails,
    storyblokProduct,
  }: ProductSources,
  artistInternalIdsBySlug: ArtistInternalIdsBySlug,
): ProductType | null => {
  if (!storyblokProduct) return null;

  if (storyblokProduct.component === 'product_v2') {
    const publicVariant = backendProduct?.variants.find(
      (variant) =>
        variant.title.toLowerCase() ===
        PRODUCT_VARIANT_TITLE_PUBLIC.toLowerCase(),
    );

    return {
      ...storyblokProduct,
      aaId: backendProduct?.id,
      shopifyVariantId: shopifyPurchaseDetails?.shopifyVariantId,
      description: pageData?.description,
      recommendations: pageData?.recommendations || [],
      worksCount: pageData?.worksCount,
      taxText: pageData?.taxText,
      pricesAndAvailability: pageData?.pricesAndAvailability,
      nftAuction: [
        {
          ...(storyblokProduct.nftAuction?.[0] || []),
          manifoldListingId: shopifyPurchaseDetails?.manifoldListingId,
          floorPrice: shopifyPurchaseDetails?.auctionFloorPrice,
        },
      ],
      title: backendProduct?.title,
      thumbnail: storyblokProduct.ogImage,
      seo: {
        title: storyblokProduct.seoTitle,
        description: storyblokProduct.seoDescription,
      },
      id: [{ id: storyblokProduct.shopifyId }],
      noCOA: shopifyPurchaseDetails?.tags.includes(PRODUCT_TAG_NO_COA) ?? false,
      details: storyblokProduct.details,
      behindTheScenesContainer: [],
      fromTheExpertsContainer: [],
      fromTheArtistContainer: [],
      productArtist: withInternalArtistIds(
        storyblokProduct.artists,
        artistInternalIdsBySlug,
      ),
      ...(backendProduct?.launchAt && {
        launchDate: shopifyToStoryblokDate(backendProduct.launchAt),
      }),
      editionSize: backendProduct?.editionSize,
      ...(backendProduct?.closeAt && {
        closingDate: shopifyToStoryblokDate(backendProduct.closeAt),
      }),
      ...(backendProduct?.releaseType && {
        releaseType: RELEASE_TYPE_MAP[backendProduct.releaseType],
      }),
      ...(backendProduct?.price && {
        price:
          publicVariant?.price.toString() || backendProduct.price.toString(),
      }),
      ...(backendProduct?.releaseType && {
        isDrawLaunch: backendProduct?.drawLaunch,
      }),
      drawReleaseIsPreAuth: true,
      productVariants: storyblokProduct.productVariants?.map(
        (productVariant) => ({
          ...productVariant,
          variants: (productVariant.variants as unknown[] | undefined)?.map(
            (productVariantItem) => {
              if (!isStoryblokVariantWithShopifyId(productVariantItem)) {
                return productVariantItem;
              }

              return {
                ...productVariantItem,
                content: {
                  ...productVariantItem.content,
                  id: [{ id: productVariantItem.content.shopifyId }],
                },
              };
            },
          ),
        }),
      ),
    } as unknown as ProductType;
  }

  return {
    ...storyblokProduct,
    productArtist: withInternalArtistIds(
      storyblokProduct.productArtist,
      artistInternalIdsBySlug,
    ),
    aaId: backendProduct?.id ?? '',
    shopifyVariantId: shopifyPurchaseDetails?.shopifyVariantId,
    title:
      backendProduct?.title ??
      shopifyPurchaseDetails?.name ??
      storyblokProduct.title,
    id: storyblokProduct.id?.length
      ? storyblokProduct.id
      : [
          {
            id:
              backendProduct?.shopifyId ??
              shopifyPurchaseDetails?.shopifyId ??
              '',
          },
        ],
    price: backendProduct?.price.toString() ?? storyblokProduct.price,
  } as unknown as ProductType;
};

const toEntry = (
  product: ProductSources,
  artistInternalIdsBySlug: ArtistInternalIdsBySlug,
): ProductContextEntry | null => {
  const key = productShopifyIdKey(product.shopifyId);
  if (!key) return null;

  return {
    ...product,
    shopifyId: key,
    productType: toProductType(
      { ...product, shopifyId: key },
      artistInternalIdsBySlug,
    ),
  };
};

const mergeProductIntoMap = (
  productsByShopifyId: Record<string, ProductContextEntry>,
  product: ProductSources,
  artistInternalIdsBySlug: ArtistInternalIdsBySlug,
) => {
  const key = productShopifyIdKey(product.shopifyId);
  if (!key) return productsByShopifyId;

  const existing = productsByShopifyId[key];
  const merged = {
    ...existing,
    ...product,
    shopifyId: key,
    storyblokProduct: product.storyblokProduct ?? existing?.storyblokProduct,
    shopifyPurchaseDetails:
      product.shopifyPurchaseDetails ?? existing?.shopifyPurchaseDetails,
    shopifyProduct: product.shopifyProduct ?? existing?.shopifyProduct,
    backendProduct: product.backendProduct ?? existing?.backendProduct,
    pageData: product.pageData ?? existing?.pageData,
  };

  return {
    ...productsByShopifyId,
    [key]: {
      ...merged,
      productType: toProductType(merged, artistInternalIdsBySlug),
    },
  };
};

const rebuildProductTypes = (
  productsByShopifyId: Record<string, ProductContextEntry>,
  artistInternalIdsBySlug: ArtistInternalIdsBySlug,
): Record<string, ProductContextEntry> =>
  Object.entries(productsByShopifyId).reduce<
    Record<string, ProductContextEntry>
  >((acc, [shopifyId, product]) => {
    acc[shopifyId] = {
      ...product,
      productType: toProductType(product, artistInternalIdsBySlug),
    };

    return acc;
  }, {});

type ProductProviderProps = {
  children: ReactNode;
  products?: ProductSources[];
};

export const ProductProvider = ({
  children,
  products = EMPTY_PRODUCT_SOURCES,
}: ProductProviderProps) => {
  const artistInternalIdsBySlugRef = useRef<ArtistInternalIdsBySlug>(new Map());
  const pendingArtistSlugsRef = useRef<Set<string>>(new Set());
  const artistsSearchGateway = useMemo(
    () => new AvantArteArtistsSearchGateway(csrClient),
    [],
  );
  const [productsByShopifyId, setProductsByShopifyId] = useState<
    Record<string, ProductContextEntry>
  >(() =>
    products.reduce<Record<string, ProductContextEntry>>((acc, product) => {
      const entry = toEntry(product, artistInternalIdsBySlugRef.current);
      if (!entry) return acc;
      acc[entry.shopifyId] = entry;
      return acc;
    }, {}),
  );

  const ensureArtistInternalIds = useCallback(
    (productsToCheck: ProductSources[]) => {
      const missingSlugs = collectArtistSlugs(productsToCheck).filter(
        (slug) =>
          !artistInternalIdsBySlugRef.current.has(slug) &&
          !pendingArtistSlugsRef.current.has(slug),
      );

      if (missingSlugs.length === 0) return;

      missingSlugs.forEach((slug) => pendingArtistSlugsRef.current.add(slug));

      void artistsSearchGateway
        .getArtistsBySlug(missingSlugs)
        .then((internalArtists) => {
          const internalArtistsBySlug = new Map(
            internalArtists
              .filter((artist) => artist.slug)
              .map((artist) => [artist.slug as string, artist.id]),
          );

          missingSlugs.forEach((slug) => {
            artistInternalIdsBySlugRef.current.set(
              slug,
              internalArtistsBySlug.get(slug) ?? null,
            );
          });

          setProductsByShopifyId((current) =>
            rebuildProductTypes(current, artistInternalIdsBySlugRef.current),
          );
        })
        .finally(() => {
          missingSlugs.forEach((slug) =>
            pendingArtistSlugsRef.current.delete(slug),
          );
        });
    },
    [artistsSearchGateway],
  );

  useEffect(() => {
    ensureArtistInternalIds(products);
  }, [ensureArtistInternalIds, products]);

  const setProduct = useCallback(
    (product: ProductSources) => {
      setProductsByShopifyId((current) =>
        mergeProductIntoMap(
          current,
          product,
          artistInternalIdsBySlugRef.current,
        ),
      );
      ensureArtistInternalIds([product]);
    },
    [ensureArtistInternalIds],
  );

  const setProducts = useCallback(
    (newProducts: ProductSources[]) => {
      setProductsByShopifyId((current) =>
        newProducts.reduce(
          (acc, product) =>
            mergeProductIntoMap(
              acc,
              product,
              artistInternalIdsBySlugRef.current,
            ),
          current,
        ),
      );
      ensureArtistInternalIds(newProducts);
    },
    [ensureArtistInternalIds],
  );

  const getProduct = useCallback(
    (shopifyId: string | null | undefined) => {
      const key = productShopifyIdKey(shopifyId);
      if (!key) return null;

      return productsByShopifyId[key] ?? null;
    },
    [productsByShopifyId],
  );

  const refreshShopifyProduct = useCallback(
    async (
      shopifyId: string,
      options: RefreshShopifyProductOptions = {},
    ): Promise<ShopifyProductPurchaseDetails | null> => {
      const key = productShopifyIdKey(shopifyId);
      if (!key) return null;

      const shopifyPurchaseDetails =
        await shopifyProductsGateway.getProductPurchaseDetails({
          identifier: { id: shopifyAdminIdToGraphqlId(key) },
          releaseName: options.releaseName ?? null,
          variantIdentifier: options.variantIdentifier,
        });

      setProduct({
        shopifyId: key,
        shopifyPurchaseDetails,
      });

      return shopifyPurchaseDetails;
    },
    [setProduct],
  );

  const refreshShopifyProducts = useCallback(
    async (
      shopifyIds: string[],
      options: RefreshShopifyProductOptions = {},
    ): Promise<void> => {
      await Promise.all(
        shopifyIds.map((shopifyId) =>
          refreshShopifyProduct(shopifyId, options),
        ),
      );
    },
    [refreshShopifyProduct],
  );

  const value = useMemo(
    () => ({
      productsByShopifyId,
      getProduct,
      setProduct,
      setProducts,
      refreshShopifyProduct,
      refreshShopifyProducts,
    }),
    [
      getProduct,
      productsByShopifyId,
      refreshShopifyProduct,
      refreshShopifyProducts,
      setProduct,
      setProducts,
    ],
  );

  return (
    <ProductContext.Provider value={value}>{children}</ProductContext.Provider>
  );
};

export const useProducts = () => useContext(ProductContext);

export const useProductByShopifyId = (shopifyId: string | null | undefined) => {
  const { getProduct } = useProducts();

  return getProduct(shopifyId);
};
